import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Session, SessionListItem, sessionApi, ChatMode } from '../services/api';

interface SessionContextType {
  currentSession: Session | null;
  sessions: SessionListItem[];
  loading: boolean;
  error: string | null;
  createNewSession: () => Promise<Session>;
  loadSession: (sessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
  updateSessionMode: (sessionId: string, mode: ChatMode) => Promise<void>;
  generateTitle: (sessionId: string) => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const createNewSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sessionApi.create();
      const newSession = response.data;
      setCurrentSession(newSession);
      // Add to sessions list as SessionListItem
      const newSessionListItem: SessionListItem = {
        session_id: newSession.session_id,
        title: newSession.title,
        created_at: newSession.created_at,
        last_activity: newSession.last_activity,
        message_count: 0,
        file_count: newSession.uploaded_files ? newSession.uploaded_files.length : 0
      };
      setSessions(prev => [newSessionListItem, ...prev]);
      return newSession;
    } catch (err) {
      setError('Failed to create session');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (sessionId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await sessionApi.get(sessionId);
      setCurrentSession(response.data);
    } catch (err) {
      setError('Failed to load session');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSessionTitle = useCallback(async (sessionId: string, title: string) => {
    try {
      console.log(`[SessionContext] Updating session ${sessionId} title to: ${title}`);
      const response = await sessionApi.update(sessionId, { title });
      
      // Update the current session if it matches
      if (currentSession?.session_id === sessionId) {
        console.log(`[SessionContext] Updating currentSession with new title`);
        setCurrentSession(response.data);
      }
      
      // Update in the sessions list
      setSessions(prev => {
        console.log(`[SessionContext] Updating sessions list`);
        return prev.map(s => 
          s.session_id === sessionId ? {
            ...s,
            title: response.data.title
          } : s
        );
      });
      console.log(`[SessionContext] Title update complete`);
    } catch (err) {
      setError('Failed to update session title');
      throw err;
    }
  }, [currentSession]);

  const updateSessionMode = useCallback(async (sessionId: string, mode: ChatMode) => {
    try {
      const response = await sessionApi.update(sessionId, { mode });
      
      // Update the current session if it matches
      if (currentSession?.session_id === sessionId) {
        setCurrentSession(response.data);
      }
      
      // Update in the sessions list - SessionListItem doesn't have mode, so just keep as is
      // The mode is only relevant for the currentSession
    } catch (err) {
      setError('Failed to update session mode');
      throw err;
    }
  }, [currentSession]);

  const generateTitle = useCallback(async (sessionId: string) => {
    try {
      console.log(`[SessionContext] Generating title for session ${sessionId}`);
      const response = await sessionApi.generateTitle(sessionId);
      console.log(`[SessionContext] Title generation response:`, response.data);
      
      if (response.data.generated) {
        console.log(`[SessionContext] Updating title to: ${response.data.title}`);
        // Update the session with the new title
        await updateSessionTitle(sessionId, response.data.title);
        console.log(`[SessionContext] Title updated successfully`);
      }
    } catch (err) {
      console.error('[SessionContext] Failed to generate title:', err);
      // Don't throw - title generation is not critical
    }
  }, [updateSessionTitle]);

  // Load sessions and restore last session on mount
  React.useEffect(() => {
    if (!initialized) {
      const loadInitialSessions = async () => {
        try {
          setLoading(true);
          
          // Load all sessions
          const response = await sessionApi.list();
          const sessionsList = response.data.sessions;
          setSessions(sessionsList);
          
          // Check if user wants to force a new session
          const urlParams = new URLSearchParams(window.location.search);
          const forceNew = urlParams.get('new') === 'true';
          
          if (forceNew) {
            // User explicitly wants a new session
            await createNewSession();
            // Remove the parameter from URL to prevent creating new sessions on refresh
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
          
          // Try to restore last session from localStorage
          const lastSessionId = localStorage.getItem('lastSessionId');
          if (lastSessionId && sessionsList.length > 0) {
            const lastSession = sessionsList.find(s => s.session_id === lastSessionId);
            if (lastSession) {
              // Load full session details
              const fullSession = await sessionApi.get(lastSession.session_id);
              setCurrentSession(fullSession.data);
            } else {
              // Last session no longer exists, use most recent
              const fullSession = await sessionApi.get(sessionsList[0].session_id);
              setCurrentSession(fullSession.data);
            }
          } else if (sessionsList.length > 0) {
            // No stored session, use most recent
            const fullSession = await sessionApi.get(sessionsList[0].session_id);
            setCurrentSession(fullSession.data);
          }
          
          // If no sessions exist, create one
          if (sessionsList.length === 0) {
            await createNewSession();
          }
        } catch (err) {
          console.error('Failed to load initial sessions:', err);
          // Create a new session as fallback
          try {
            await createNewSession();
          } catch (createErr) {
            setError('Failed to initialize session');
          }
        } finally {
          setLoading(false);
          setInitialized(true);
        }
      };
      
      loadInitialSessions();
    }
  }, [initialized]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Store current session ID when it changes
  React.useEffect(() => {
    if (currentSession) {
      localStorage.setItem('lastSessionId', currentSession.session_id);
    }
  }, [currentSession]);

  const value: SessionContextType = {
    currentSession,
    sessions,
    loading,
    error,
    createNewSession,
    loadSession,
    updateSessionTitle,
    updateSessionMode,
    generateTitle,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};